import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import {
	AudioTechnology,
	MonetizationType,
	ObjectType,
	OfferType,
	PresentationType,
	VideoTechnology,
} from '@/@types/graphql-types';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { BuyboxParam, getTranslatableForShowOfferGraphql } from '@/helpers/offer-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { shouldClickMask, clickMaskUrl } from '@/components/click-masking/click-masking-config';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';

import { useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { ModalHelper } from '@/helpers/modal-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { diffInDays } from '@/helpers/date-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { SponsoredClickoutsHelper } from '@/helpers/sponsored-clickouts-helper';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { getPromoText } from '@/helpers/provider-promotion';


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxOfferNew',
  props: {
    offer: null,
    title: null,
    isPromotedBuyBox: { type: Boolean },
    isBundle: { type: Boolean },
    isEpisode: { type: Boolean },
    additionalContexts: null
  },
  emits: ["clickOut"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		data: {
			buyboxOffer: TitleOfferFragment & { bundleId?: number };
			isBundle: boolean;
			isLeavingIconShown: boolean;
			row: HTMLElement | null;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	offer: TitleOfferFragment & {
		bundleId?: number;
		icons?: string[];
		availableFromTime?: string;
		offerSeasons?: string[];
	};
	title: TitleDetail;
	isPromotedBuyBox?: boolean;
	isBundle?: boolean;
	isEpisode?: boolean;
	additionalContexts: SnowplowContext[];
};

const BuyBoxInfoModal = () => import('@/components/buybox-new/BuyBoxInfoModal.vue');
const BuyBoxBundleIcons = () => import('@/components/buybox-new/BuyBoxBundleIcons.vue');





const { country, language, webLocale } = useLanguageStore();
const { t, tc } = useI18n();
const { isArbitrating, withArbitrageTracking, trackTagEvent } = useArbitrage();

const displayBuyBoxInfoModal = ref<boolean>(false);
const offerContainer = ref<HTMLElement | null>(null);

const { onIntersection } = useOfferImpressionTracking({ additionalContexts: props.additionalContexts });

const providerPromo = computed(() => getPromoText(country.value, [props.offer]));

const isProviderPromo = computed(() => {
	return (
		props.offer.package.packageId === providerPromo.value?.provider &&
		!props.offer.bundleId &&
		providerPromo.value != null
	);
});

const isModalProvider = computed(() => ['justwatchtv', 'plexplayer'].includes(props.offer.package.technicalName));

const formattedDate = computed(() => {
	const ESTTimeZone = 'America/New_York';
	const PSTTimeZone = 'America/Los_Angeles';

	if (!props.offer?.availableFromTime) {
		return null;
	}

	return {
		Day: new Intl.DateTimeFormat(`${language.value}-${country.value}`, {
			day: 'numeric',
			month: 'short',
			timeZone: ESTTimeZone,
		}).format(new Date(props.offer.availableFromTime)),
		PST:
			new Intl.DateTimeFormat(`${language.value}-${country.value}`, {
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: PSTTimeZone,
			}).format(new Date(props.offer.availableFromTime)) + ' PST',
		EST:
			new Intl.DateTimeFormat(`${language.value}-${country.value}`, {
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: ESTTimeZone,
			}).format(new Date(props.offer.availableFromTime)) + ' EST',
	};
});

// Amazon streaming bounty redirects
const shouldRedirectForStreamBounty = computed(
	() =>
		['FR', 'IT', 'ES', 'CA', 'BR', 'AU', 'NL', 'PL', 'SE', 'NZ'].includes(country.value) &&
		(props.offer.package.packageId === 119 || props.offer.package.packageId === 9)
);

const shouldMaskClicks = computed(() => shouldClickMask(props.offer?.package.packageId, country.value));

const offerUrl = computed(() => {
	if (shouldMaskClicks.value) return clickMaskUrl(props.offer.standardWebURL!);

	if (isArbitrating.value) {
		const arbitrageUrl = withArbitrageTracking(() => props.offer);
		if (arbitrageUrl) return arbitrageUrl.toString();
	}

	if (isProviderPromo.value && providerPromo.value?.url) return providerPromo.value.url;

	const url = new ClickoutUrl(props.offer)
		.addContexts(new SnowplowTitleContextGraphql(props.title.objectId, props.title.objectType))
		.set('uct_country', country.value)
		.set('uct_buybox', props.isPromotedBuyBox ? BuyboxParam.Promoted : BuyboxParam.Normal);

	// Amazon streaming bounty redirects
	if (shouldRedirectForStreamBounty.value) {
		return url.toString().replace('app.primevideo.com', 'www.primevideo.com');
	}

	return url.toString();
});

const isLeavingIconShown = computed(() => {
	return (
		!!props.offer.availableTo &&
		diffInDays(new Date(props.offer.availableTo)) < 30 &&
		diffInDays(new Date(props.offer.availableTo)) >= 0
	);
});

const leavingLabel = computed(() => {
	if (!isLeavingIconShown.value) return '';
	const diffDays = diffInDays(new Date(props.offer.availableTo));
	if (diffDays === 0) return tc('WEBAPP_LEAVING_TODAY');
	if (diffDays === 1) return tc('WEBAPP_LEAVING_TOMORROW');
	return t('WEBAPP_LEAVING_X_DAYS', { days: diffDays });
});

const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

const audioLanguages = computed(() => {
	if (!props.offer?.audioLanguages) return false;
	return languageArrayToString(props?.offer?.audioLanguages);
});

const currency = computed(() => {
	if (country.value === 'GB') return 'GBP';
	else if (country.value === 'IN') return 'INR';
	else if (['DE', 'FR', 'ES'].includes(country.value)) return 'EUR';
	else if (country.value === 'CA') return 'CAD';
	else if (country.value === 'AU') return 'AUD';
	else if (country.value === 'MX') return 'MXN';
	else if (country.value === 'TR') return 'TRY';
	else if (country.value === 'BR') return 'BRL';
	else return 'USD';
});

function formatCurrency(price: number) {
	const locale = language.value.includes('-') ? language.value : `${language.value}-${country.value}`;

	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency: currency.value,
	}).format(price);
}

const selectedPackagePlan = computed(() => {
	if (props.offer?.package.planOffers.length > 0) {
		const plan = props.offer.package.planOffers[0];
		// if a plan has a children that means it's a free trial plan and that always has priority.
		if (plan.children.length > 0) {
			return { ...plan.children[0], retailPrice: plan.retailPrice };
		}
		return plan;
	}
	return null;
});

const retailPrice = computed(() => {
	if (props.isBundle) {
		return null;
	} else if (props.offer?.retailPrice) {
		return props.offer?.retailPrice;
	} else if (selectedPackagePlan.value && selectedPackagePlan.value.retailPrice) {
		// Retail price from API
		return t(
			props.offer.monetizationType === MonetizationType.Flatrate && hasfreeTrial.value
				? 'WEBAPP_THEN_X_PER_MONTH'
				: 'WEBAPP_X_PER_MONTH',
			{ price: selectedPackagePlan.value.retailPrice }
		);
	}
	return null;
});

const retailPriceBreakdown = computed(() => {
	if (
		props.offer.monetizationType === MonetizationType.Buy &&
		(props.title.objectType === ObjectType.Show || props.title.objectType === ObjectType.ShowSeason) &&
		props.offer.retailPriceValue &&
		props.offer.elementCount &&
		props.offer.elementCount > 1
	) {
		const pricePerItem = Number(
			(Math.trunc((props.offer.retailPriceValue / props.offer.elementCount) * 100) / 100).toFixed(2)
		);

		return `${formatCurrency(pricePerItem)} ${
			props.title.objectType === ObjectType.Show ? `/ ${tc('WEBAPP_SEASON')}` : `/ ${tc('WEBAPP_EPISODE')}`
		}`;
	}
	return '';
});

const isShowPageTVOD = computed(
	() =>
		[MonetizationType.Buy, MonetizationType.Rent].includes(props.offer.monetizationType) &&
		props.title.objectType === ObjectType.Show &&
		!props.isEpisode
);

const showPageTVODBestEpisodePrice = computed(() => {
	if (!isShowPageTVOD.value || !props.offer.offerSeasons) return null;
	if (!['en', 'de', 'fr', 'it', 'es', 'pt', 'tr'].includes(language.value)) return null;

	return minEpisodePricePerSeason.value[0] ? formatCurrency(minEpisodePricePerSeason.value[0]) : null;
});

const minEpisodePricePerSeason = computed(() => {
	const seasonIdsOnOffer = props.offer.offerSeasons ?? [];

	if (props.title.__typename !== 'Show' || !seasonIdsOnOffer.length) return [null];

	const filteredSeasons = props.title.seasons.filter(season => seasonIdsOnOffer.includes(season.id));

	const seasonPrices = filteredSeasons.map(season => {
		const validOffers = season.offers.filter(
			offer =>
				offer.monetizationType === props.offer.monetizationType &&
				offer.package.shortName === props.offer.package.shortName &&
				offer.type === OfferType.Aggregated &&
				offer.minRetailPrice &&
				offer.minRetailPrice > 0
		);

		return validOffers.map(offer => offer.minRetailPrice);
	});

	return seasonPrices
		.filter(priceArray => priceArray.length > 0) // Keep only non-empty arrays
		.flat() // Flatten the array of arrays
		.filter(price => price != null)
		.sort((a, b) => a - b);
});

const showRetailPrice = computed(() => !!retailPrice.value && props.offer?.monetizationType !== MonetizationType.Free);

const hasfreeTrial = computed(
	() => !!freeTrialText.value && props.offer?.monetizationType === MonetizationType.Flatrate
);

const freeTrialText = computed(() => {
	if (props.isBundle) {
		return null;
	}
	// Free trial data from API
	if (selectedPackagePlan.value?.isTrial) {
		if (selectedPackagePlan.value.durationDays === 1) return t('WEBAPP_1_DAY_FREE');
		return t('WEBAPP_X_DAYS_FREE', {
			days: selectedPackagePlan.value.durationDays,
		});
	}
	return null;
});

const presentationTypeLabel = computed(() => {
	switch (props.offer?.presentationType) {
		case PresentationType.Hd:
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'DVD';
		case PresentationType.Bluray:
			return 'Blu-Ray';
		default:
			return '';
	}
});

const ageRating = computed(() => {
	if (props.title.__typename !== 'Season') {
		return props.title.content.ageCertification;
	}
	return null;
});

const dolbyTechnology = computed(() => {
	return (
		props.offer?.videoTechnology.includes(VideoTechnology.DolbyVision) ||
		props.offer?.audioTechnology.includes(AudioTechnology.Dolby_5Point_1) ||
		props.offer?.audioTechnology.includes(AudioTechnology.DolbyAtmos)
	);
});

const showText = computed(() => getTranslatableForShowOfferGraphql(props.offer, props.title.objectType));

const runtimeMins = computed(() => (props.title?.content?.runtime ? `${props.title.content.runtime}min` : null));

const highlightCta = computed(() => props.offer?.monetizationType === MonetizationType.Flatrate && !props.isBundle);

const isSponsoredOffer = computed(() =>
	SponsoredClickoutsHelper.isSponsoredOfferGraphql(props.offer?.package.packageId, webLocale.value)
		? 'nofollow sponsored noopener'
		: 'nofollow noopener'
);

const playerModalByProvider: Record<string, { cmp: () => Promise<any> }> = {
	plexplayer: {
		cmp: () => import('@/ui-components/PlexPlayerModal.vue'),
	},
	justwatchtv: {
		cmp: () => import('@/ui-components/JwTVModal.vue'),
	},
};

function openModal() {
	if (!isModalProvider.value) return;
	const modalData = playerModalByProvider[props.offer.package.technicalName];
	ModalHelper.openModal(
		modalData.cmp,
		{ offer: props.offer, title: props.title },
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);
	handleClickOut();
}

function toggleBuyBoxInfoModal() {
	displayBuyBoxInfoModal.value = !displayBuyBoxInfoModal.value;

	if (displayBuyBoxInfoModal.value) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{ action: 'more_info_clicked', label: 'buybox' },
			props.additionalContexts
		);
	}

	//TITLE_DETAIL_REDESIGN_NEW_BUYBOX
	// trackTitleDetailRedesignNewBuyBoxExp({ action: 'click', label: 'more_info_clicked' });
	//TITLE_DETAIL_REDESIGN_NEW_BUYBOX
}

function handleClickOut() {
	trackTagEvent(() => props.offer);

	emit('clickOut', {
		buyboxOffer: props.offer,
		isBundle: !!props.isBundle,
		isLeavingIconShown: isLeavingIconShown.value,
		row: offerContainer.value,
	});
}

function onVisibilityChanged(isVisible: boolean) {
	onIntersection({ ...props.offer, isLeavingIconShown: isLeavingIconShown.value }, isVisible);
}

function languageArrayToString(languageArray: string[]) {
	const languages = languageArray.map(language => {
		return {
			language,
			name: tc(`WEBAPP_LANGUAGE_${language.toUpperCase()}`),
		};
	});
	const filteredLanguages = languages.filter((language: { language: string; name: string }) => {
		return !language.name.startsWith('WEBAPP_LANGUAGE_');
	});
	return filteredLanguages
		.map((language: { language: string; name: string }) => {
			return language.name;
		})
		.join(', ');
}

function offerLabel(monetizationType: MonetizationType) {
	if (isProviderPromo.value) return providerPromo.value!.offerText;

	const monetizationTypeMapping = {
		ADS: tc('WEBAPP_FREE_WITH_ADS'),
		BUY: tc('WEBAPP_BUY'),
		CINEMA: tc('WEBAPP_CINEMA'),
		FAST: tc('WEBAPP_FREE_TV'),
		FLATRATE: tc('WEBAPP_SUBSCRIPTION'),
		FLATRATE_AND_BUY: tc('WEBAPP_BUY'),
		FREE: tc('WEBAPP_FREE'),
		RENT: tc('WEBAPP_RENT'),
	};
	if (props.isBundle) {
		return tc('WEBAPP_BUNDLE');
	}

	if (monetizationType === MonetizationType.Fast && formattedDate.value?.['Day']) {
		return formattedDate.value?.['Day'];
	}

	return monetizationTypeMapping[monetizationType];
}

return { __sfc: true,BuyBoxInfoModal, BuyBoxBundleIcons, props, emit, country, language, webLocale, t, tc, isArbitrating, withArbitrageTracking, trackTagEvent, displayBuyBoxInfoModal, offerContainer, onIntersection, providerPromo, isProviderPromo, isModalProvider, formattedDate, shouldRedirectForStreamBounty, shouldMaskClicks, offerUrl, isLeavingIconShown, leavingLabel, observeVisibilityOptions, audioLanguages, currency, formatCurrency, selectedPackagePlan, retailPrice, retailPriceBreakdown, isShowPageTVOD, showPageTVODBestEpisodePrice, minEpisodePricePerSeason, showRetailPrice, hasfreeTrial, freeTrialText, presentationTypeLabel, ageRating, dolbyTechnology, showText, runtimeMins, highlightCta, isSponsoredOffer, playerModalByProvider, openModal, toggleBuyBoxInfoModal, handleClickOut, onVisibilityChanged, languageArrayToString, offerLabel, ClickoutAnchor, ProviderIcon }
}

})